import "./styles.scss";

import { Text, Button, Icon } from "@fragments";
import { UserAvatar } from "@components/Community";
import {
  IconArrowFork,
  IconArrowRight,
  IconPercentage,
  IconUsers,
  IconMail,
  IconPhoto,
  IconAlertTriangle,
  IconLinkOff,
  IconCircleCheck,
  IconCircleX,
  IconWallet,
} from "@tabler/icons-react";

import { SPLIT_INVITE_STATE } from "../enums/splitInviteStates";

/**
 * Private split invite (§2). A centered, elevated card rendered INSIDE the app
 * shell (the user is in-app, like /banking onboarding) — no brand InviteShell.
 * Shows the community, the offered percentile and a gated accept/decline area,
 * plus self-contained status screens for terminal / wrong-account outcomes.
 */
export default function SplitInviteView(props) {
  const { state } = props;

  let content;
  if (state === SPLIT_INVITE_STATE.LOADING) content = <InviteSkeleton />;
  else if (state === SPLIT_INVITE_STATE.VALID) content = <InviteCard {...props} />;
  else if (state === SPLIT_INVITE_STATE.WRONG_EMAIL) content = <WrongEmail {...props} />;
  else content = <InviteStatus state={state} />;

  return (
    <div className="split-invite">
      <div className="si-main">{content}</div>
    </div>
  );
}

// ── The live invite card (VALID) ───────────────────────────────────────────────
function InviteCard({
  community,
  owner,
  invitedEmail,
  percentile,
  awaitingWallet,
  submitting,
  canRespond,
  accept,
  decline,
}) {
  const membersCount = community?.members_count;

  return (
    <article className="si-card">
      {/* Banner */}
      <div className="si-banner" style={{ "--i": 0 }}>
        {community?.banner ? (
          <img src={community.banner} alt={community?.name || "Comunidade"} loading="lazy" />
        ) : (
          <div className="si-banner-fallback">
            <IconPhoto size={30} />
          </div>
        )}
      </div>

      <div className="si-body">
        <span className="si-eyebrow" style={{ "--i": 1 }}>
          <Text.span contrast={6} left={<IconArrowFork size={14} />}>
            Convite de repasse de receita
          </Text.span>
        </span>

        <div className="si-title" style={{ "--i": 2 }}>
          <Text.h2 contrast={9} weight={500}>
            {community?.name || "Comunidade"}
          </Text.h2>
        </div>

        {/* Community meta — members + owner */}
        <div className="si-meta" style={{ "--i": 3 }}>
          {membersCount != null && (
            <Text.span contrast={5} left={<IconUsers size={15} />}>
              {membersCount} {Number(membersCount) === 1 ? "membro" : "membros"}
            </Text.span>
          )}
          {owner?.name && (
            <div className="si-owner">
              <UserAvatar
                user={{ username: owner.name, avatar: owner.avatar }}
                size={24}
              />
              <Text.span contrast={6}>{owner.name}</Text.span>
            </div>
          )}
        </div>

        {/* Offered share */}
        <div className="si-terms" style={{ "--i": 4 }}>
          <div className="si-term">
            <Text.span contrast={5} left={<IconPercentage size={14} />}>
              Repasse oferecido
            </Text.span>
            <Text.span contrast={9} weight={500}>
              {percentile != null ? `${percentile}%` : "—"}
            </Text.span>
          </div>
        </div>

        {/* Who it's for */}
        <div className="si-invited" style={{ "--i": 5 }}>
          <span className="si-invited-icon">
            <IconMail size={15} />
          </span>
          <Text.span contrast={5}>
            Convite enviado para <strong>{invitedEmail}</strong>
          </Text.span>
        </div>

        {awaitingWallet && (
          <div className="si-wallet-hint" style={{ "--i": 6 }}>
            <span className="si-wallet-icon">
              <IconWallet size={16} />
            </span>
            <Text.span contrast={6}>
              Para concluir, você precisa de uma conta bancária aprovada. Ao aceitar,
              levamos você para finalizar o cadastro.
            </Text.span>
          </div>
        )}

        <div className="si-divider" />

        {/* Gated actions */}
        <div className="si-actions" style={{ "--i": 7 }}>
          <Button
            text="Aceitar convite"
            color="primary"
            size="large"
            endIcon={<IconArrowRight size={18} />}
            onClick={() => accept()}
            disabled={!canRespond || submitting}
            fullWidth
          />
          <Button
            text="Recusar"
            color="overlay"
            variant="rounded"
            size="large"
            onClick={decline}
            disabled={!canRespond || submitting}
            preventLoading
            fullWidth
          />
        </div>
      </div>
    </article>
  );
}

// ── Wrong-account screen (signed in as someone else) ───────────────────────────
function WrongEmail({ accountEmail, switchAccount }) {
  return (
    <article className="si-card si-card--status">
      <div className="si-status">
        <Icon.Sphere size={76}>
          <IconAlertTriangle />
        </Icon.Sphere>
        <Text.h3 contrast={8} weight={500}>
          Conta diferente do convite
        </Text.h3>
        <Text.span contrast={5} className="si-status-sub">
          Este convite foi enviado para outro e-mail. Você está conectado como{" "}
          <strong>{accountEmail}</strong>. Entre com a conta certa para aceitar ou
          recusar.
        </Text.span>
        <Button
          text="Entrar com outra conta"
          color="inverse"
          variant="rounded"
          onClick={switchAccount}
          preventLoading
        />
      </div>
    </article>
  );
}

// ── Terminal / status screens ───────────────────────────────────────────────────
const STATUS_COPY = {
  [SPLIT_INVITE_STATE.ACCEPTED]: {
    Icon: IconCircleCheck,
    title: "Convite aceito!",
    subtitle:
      "Tudo certo — você agora faz parte do split de receita desta comunidade. Você pode acompanhar seus recebimentos no Banking.",
    cta: { label: "Ir para o Banking", href: "/banking" },
  },
  [SPLIT_INVITE_STATE.NOT_FOUND]: {
    Icon: IconLinkOff,
    title: "Convite não encontrado",
    subtitle:
      "Este link de convite não existe ou foi removido. Confira o endereço ou peça um novo convite ao dono da comunidade.",
    cta: { label: "Ir para a Ginastee", href: "/" },
  },
  [SPLIT_INVITE_STATE.EXPIRED]: {
    Icon: IconAlertTriangle,
    title: "Convite expirado",
    subtitle:
      "O prazo para responder a este convite acabou. Peça um novo convite ao dono da comunidade.",
    cta: { label: "Ir para a Ginastee", href: "/" },
  },
  [SPLIT_INVITE_STATE.USED]: {
    Icon: IconCircleCheck,
    title: "Convite indisponível",
    subtitle:
      "Este convite já foi respondido ou não está mais ativo. Se você aceitou, acesse o Banking para acompanhar.",
    cta: { label: "Ir para o Banking", href: "/banking" },
  },
  [SPLIT_INVITE_STATE.DECLINED]: {
    Icon: IconCircleX,
    title: "Convite recusado",
    subtitle:
      "Você recusou este convite de split. O dono da comunidade foi avisado. Você já pode fechar esta página.",
    cta: { label: "Ir para a Ginastee", href: "/" },
  },
  [SPLIT_INVITE_STATE.ERROR]: {
    Icon: IconAlertTriangle,
    title: "Algo deu errado",
    subtitle:
      "Não foi possível carregar este convite agora. Tente novamente em alguns instantes.",
    cta: { label: "Tentar novamente", reload: true },
  },
};

function InviteStatus({ state }) {
  const copy = STATUS_COPY[state] ?? STATUS_COPY[SPLIT_INVITE_STATE.ERROR];
  const { Icon: StatusIcon, title, subtitle, cta } = copy;

  function onCta() {
    if (cta?.reload) window.location.reload();
    else if (cta?.href) window.location.href = cta.href;
  }

  return (
    <article className="si-card si-card--status">
      <div className="si-status">
        <Icon.Sphere size={76}>
          <StatusIcon />
        </Icon.Sphere>
        <Text.h3 contrast={8} weight={500}>
          {title}
        </Text.h3>
        <Text.span contrast={5} className="si-status-sub">
          {subtitle}
        </Text.span>
        {cta && (
          <Button
            text={cta.label}
            color="inverse"
            variant="rounded"
            onClick={onCta}
            preventLoading
          />
        )}
      </div>
    </article>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function InviteSkeleton() {
  return (
    <article className="si-card is-skeleton" aria-busy="true">
      <div className="si-banner skel" />
      <div className="si-body">
        <div className="skel skel-line" style={{ width: "40%", height: 20 }} />
        <div className="skel skel-line" style={{ width: "70%", height: 28 }} />
        <div className="skel skel-line" style={{ width: "55%", height: 18 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 56 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 48 }} />
      </div>
    </article>
  );
}
